import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Shield, Users, Crown, Trash2, Search, Flag, BarChart3, FileWarning, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersMeta, setUsersMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [reports, setReports] = useState([]);
  const [posts, setPosts] = useState([]);
  const [postsMeta, setPostsMeta] = useState({ total: 0, page: 1 });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers });
      setStats(res.data);
    } catch { toast.error('Failed to load stats'); }
  };

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (roleFilter && roleFilter !== 'all') params.append('role', roleFilter);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers });
      setUsers(res.data.users);
      setUsersMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch { toast.error('Failed to load users'); }
  }, [search, roleFilter]);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/admin/reports`, { headers });
      setReports(res.data);
    } catch { toast.error('Failed to load reports'); }
  };

  const fetchPosts = async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/posts?page=${page}`, { headers });
      setPosts(res.data.posts);
      setPostsMeta({ total: res.data.total, page: res.data.page });
    } catch { toast.error('Failed to load posts'); }
  };

  useEffect(() => {
    if (tab === 'users') fetchUsers();
    if (tab === 'reports') fetchReports();
    if (tab === 'content') fetchPosts();
  }, [tab, fetchUsers]);

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/role`, { user_id: userId, role }, { headers });
      toast.success(`Role updated to ${role}`);
      fetchUsers(usersMeta.page);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const togglePremium = async (userId, current) => {
    try {
      await axios.put(`${API}/admin/users/premium`, { user_id: userId, is_premium: !current }, { headers });
      toast.success(`Premium ${!current ? 'granted' : 'revoked'}`);
      fetchUsers(usersMeta.page);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success('User deleted');
      fetchUsers(usersMeta.page);
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const postAction = async (postId, action) => {
    try {
      await axios.post(`${API}/admin/posts/action`, { post_id: postId, action }, { headers });
      toast.success(action === 'delete' ? 'Post deleted' : 'Post flagged');
      fetchPosts(postsMeta.page);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'content', label: 'Content', icon: FileWarning },
    { id: 'reports', label: 'Reports', icon: Flag },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-coral" />
            <h1 className="text-4xl font-bold">Admin Dashboard</h1>
          </div>
          <Button
            data-testid="analytics-link"
            onClick={() => navigate('/admin/analytics')}
            className="bg-coral text-white rounded-full px-6"
          >
            <BarChart3 className="w-4 h-4 mr-2" /> View Analytics
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map(t => (
            <Button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2 font-semibold flex items-center space-x-2 ${
                tab === t.id ? 'bg-coral text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <t.icon className="w-4 h-4" />
              <span>{t.label}</span>
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-stats">
            {[
              { label: 'Total Users', value: stats.total_users, color: 'bg-blue-50 text-blue-700' },
              { label: 'Premium Users', value: stats.premium_users, color: 'bg-yellow-50 text-yellow-700' },
              { label: 'Total Posts', value: stats.total_posts, color: 'bg-green-50 text-green-700' },
              { label: 'Reports', value: stats.total_reports, color: 'bg-red-50 text-red-700' },
              { label: 'Events', value: stats.total_events, color: 'bg-purple-50 text-purple-700' },
              { label: 'Payments', value: stats.total_payments, color: 'bg-teal-50 text-teal-700' },
              { label: 'Admins', value: stats.roles?.admin || 0, color: 'bg-orange-50 text-orange-700' },
              { label: 'Regular Users', value: stats.roles?.user || 0, color: 'bg-indigo-50 text-indigo-700' },
            ].map(s => (
              <div key={s.label} className={`rounded-2xl p-5 ${s.color}`}>
                <p className="text-sm font-medium opacity-80">{s.label}</p>
                <p className="text-3xl font-bold mt-1">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div>
            <div className="flex flex-col md:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="admin-user-search"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchUsers()}
                  className="pl-10"
                />
              </div>
              <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); }}>
                <SelectTrigger className="w-40" data-testid="admin-role-filter">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="tester">Tester</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => fetchUsers()} className="bg-coral text-white rounded-full">
                <Search className="w-4 h-4 mr-1" /> Search
              </Button>
            </div>

            <div className="glass-dark rounded-2xl overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-semibold">Name</th>
                    <th className="text-left p-3 font-semibold hidden md:table-cell">Email</th>
                    <th className="text-left p-3 font-semibold">Role</th>
                    <th className="text-left p-3 font-semibold">Premium</th>
                    <th className="text-right p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50/50" data-testid={`admin-user-row-${u.id}`}>
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3 text-gray-500 hidden md:table-cell">{u.email}</td>
                      <td className="p-3">
                        <Select value={u.role || 'user'} onValueChange={v => updateRole(u.id, v)}>
                          <SelectTrigger className="w-28 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="tester">Tester</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant={u.is_premium ? 'default' : 'outline'}
                          className={`h-7 text-xs rounded-full ${u.is_premium ? 'bg-yellow-500 text-white' : ''}`}
                          onClick={() => togglePremium(u.id, u.is_premium)}
                        >
                          <Crown className="w-3 h-3 mr-1" />
                          {u.is_premium ? 'Premium' : 'Free'}
                        </Button>
                      </td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => deleteUser(u.id, u.name)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-gray-500">{usersMeta.total} users total</span>
              <div className="flex items-center space-x-2">
                <Button size="sm" variant="outline" disabled={usersMeta.page <= 1} onClick={() => fetchUsers(usersMeta.page - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">Page {usersMeta.page} of {usersMeta.pages}</span>
                <Button size="sm" variant="outline" disabled={usersMeta.page >= usersMeta.pages} onClick={() => fetchUsers(usersMeta.page + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Content Moderation Tab */}
        {tab === 'content' && (
          <div className="space-y-4" data-testid="admin-content">
            {posts.map(p => (
              <div key={p.id} className="glass-dark rounded-2xl p-4 flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{p.user_name}</p>
                  <p className="text-gray-600 text-sm mt-1 line-clamp-2">{p.content}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(p.created_at).toLocaleDateString()}</p>
                  {p.flagged && <Badge className="bg-red-100 text-red-700 mt-1">Flagged</Badge>}
                </div>
                <div className="flex space-x-2 ml-3 flex-shrink-0">
                  <Button size="sm" variant="outline" className="text-yellow-600 h-8" onClick={() => postAction(p.id, 'flag')}>
                    <Flag className="w-3 h-3 mr-1" /> Flag
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 h-8" onClick={() => postAction(p.id, 'delete')}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
            {posts.length === 0 && <p className="text-center text-gray-500 py-8">No posts found</p>}
          </div>
        )}

        {/* Reports Tab */}
        {tab === 'reports' && (
          <div className="space-y-4" data-testid="admin-reports">
            {reports.length === 0 && <p className="text-center text-gray-500 py-8">No reports</p>}
            {reports.map(r => (
              <div key={r.id} className="glass-dark rounded-2xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Badge className="bg-red-100 text-red-700">Report</Badge>
                  <span className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm"><strong>{r.reporter_name}</strong> reported <strong>{r.reported_name}</strong></p>
                <p className="text-sm text-gray-600 mt-1">Reason: {r.reason}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
